import { createRoute, Link, useSearch } from "@tanstack/react-router";
import { Eye } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopCases } from "./desktop/cases.tsx";
import { SubPage } from "../components/layout.tsx";
import { Empty, ErrorBlock, Img, Price, Spinner } from "../components/ui.tsx";
import { useCaseStyles, useCases } from "../api/queries.ts";
import { useSpaces } from "../lib/site-config.ts";

/**
 * 装修案例列表 —— 内容带货的入口。
 *
 * 建材是低频高客单的生意，用户往往不知道"我家该买什么"。
 * 案例的作用是把商品放进一个具体的家里，比商品列表页更容易让人下决心。
 */

/*
  ⚠️ 空间这一排以前在这儿又写了一份，而且**漏了阳台** ——
     后台给案例打了「阳台」标签，前台却没有那一格，等于打了也筛不到。
     现在跟首页共用后台配的那份，前面补一个「全部」。
*/
function Cases() {
  const search = useSearch({ from: "/cases" }) as { space?: string; style?: string };
  const list = useCases({ space: search.space, style: search.style });
  const styles = useCaseStyles();
  const spaceTabs = [{ key: "", label: "全部" }, ...useSpaces()];

  return (
    <SubPage title="装修案例">
      <div className="sticky top-0 z-10 bg-white">
        <div className="flex gap-2 overflow-x-auto px-4 py-2.5">
          {spaceTabs.map((s) => (
            <Link
              key={s.key}
              to="/cases"
              search={{ ...search, space: s.key || undefined }}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
                (search.space ?? "") === s.key
                  ? "bg-brand-600 text-white"
                  : "bg-neutral-100 text-ink-500"
              }`}
            >
              {s.label}
            </Link>
          ))}
        </div>
        {/* 风格是从已发布的案例里现取的，没案例时这一行自动消失 */}
        {styles.data && styles.data.length > 0 && (
          <div className="flex gap-2 overflow-x-auto border-t border-neutral-100 px-4 py-2">
            <Link
              to="/cases"
              search={{ ...search, style: undefined }}
              className={`shrink-0 rounded px-2 py-1 text-xs ${
                search.style ? "text-ink-500" : "text-brand-600"
              }`}
            >
              不限风格
            </Link>
            {styles.data.map((st) => (
              <Link
                key={st}
                to="/cases"
                search={{ ...search, style: st }}
                className={`shrink-0 rounded px-2 py-1 text-xs ${
                  search.style === st ? "text-brand-600" : "text-ink-500"
                }`}
              >
                {st}
              </Link>
            ))}
          </div>
        )}
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="还没有案例" hint="平台正在整理，先去看看商品吧" />
      ) : (
        <div className="mt-2 space-y-2">
          {list.data.items.map((c) => (
            <Link
              key={c.id}
              to="/cases/$slug"
              params={{ slug: c.slug }}
              className="block bg-white"
            >
              {/* ⚠️ 用 Img 而不是裸 img：图挂了要退回灰底占位，破图比空白还难看 */}
              <Img src={c.coverUrl} alt={c.title} className="aspect-[4/3] w-full object-cover" />
              <div className="px-4 py-3">
                <p className="text-sm font-medium leading-snug">{c.title}</p>
                <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-500">
                  {c.style && <span>{c.style}</span>}
                  {c.areaSqm !== null && <span>{c.areaSqm} ㎡</span>}
                  {c.budget !== null && (
                    <span>
                      预算 <Price cents={c.budget} />
                    </span>
                  )}
                  <span className="flex items-center gap-0.5">
                    <Eye className="size-3" />
                    {c.viewCount}
                  </span>
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function CasesRoute() {
  return useIsDesktop() ? <DesktopCases /> : <Cases />;
}

export const casesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases",
  component: CasesRoute,
  validateSearch: (s: Record<string, unknown>): { space?: string; style?: string } => ({
    space: s.space ? String(s.space) : undefined,
    style: s.style ? String(s.style) : undefined,
  }),
});
